import { useState } from 'react';

import { Grid, Stack, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import useSWR from 'swr';

import { Card, FileCard, InputField, NotificationCard, Stepper, Tooltip } from '~components';
import Pager from '~components/containers/pager';
import CustomDialog from '~components/dialogs/customDialog';
import VectorImage from '~components/media/vectorImage';
import { pagePathSegments } from '~constants';
import { StatusVariant } from '~enums';
import { cancellationMiddleware } from '~lib/swr';
import ApiError from '~services/apiError';
import { getService } from '~services/serviceFactory';
import { Dimens } from '~themes';
import { triggerBlobDownload } from '~utils/fileUtils';

import WebhooksService from '../services/webhooksService';

const webhooksService = getService(WebhooksService);

const CreateWebhookContainer = () => {
	const { t } = useTranslation();
	const { palette } = useTheme();
	const navigate = useNavigate();

	const colSize = { xs: 12, md: 5 };
	const [currentPage, setCurrentPage] = useState(0);
	const [clientId, setClientId] = useState('');
	const [scope, setScope] = useState('');
	const [tokenEndpoint, setTokenEndpoint] = useState('');
	const [webhookEndpoint, setWebhookEndpoint] = useState('');
	const [hasSubmitted, setHasSubmitted] = useState(false);
	const [isUpdatingWebhook, setIsUpdatingWebhook] = useState(false);
	const [showWebhookResultDialog, setShowWebhookResultDialog] = useState(false);
	const [updateError, setUpdateError] = useState<ApiError | undefined>(undefined);

	const { data: certificate } = useSWR(
		webhooksService.getSkopeiConnectPublicKeyCertificateCacheKey(),
		(_args: [string], options: { signal?: AbortSignal }) => {
			return webhooksService.getSkopeiConnectPublicKeyCertificate(options.signal);
		},
		{ use: [cancellationMiddleware] }
	);

	const createWebhook = () => {
		setHasSubmitted(true);
		if (
			clientId.length == 0 ||
			scope.length == 0 ||
			tokenEndpoint.length == 0 ||
			webhookEndpoint.length == 0
		) {
			return;
		}

		setIsUpdatingWebhook(true);
		webhooksService
			.updateRentalWebhook({
				clientId: clientId,
				scope: scope,
				tokenEndpoint: 'https://' + tokenEndpoint,
				webhookEndpoint: 'https://' + webhookEndpoint,
			})
			.then(() => {
				setUpdateError(undefined);
			})
			.catch((error: ApiError) => {
				setUpdateError(error);
			})
			.finally(() => {
				setShowWebhookResultDialog(true);
				setIsUpdatingWebhook(false);
			});
	};

	const steps = [
		t('webhook.labels.addCertificate', { ns: 'entities' }),
		t('webhook.labels.webhookConfiguration', { ns: 'entities' }),
	];
	const pages = [
		{
			key: 'step 1',
			content: (
				<Grid container columnSpacing={Dimens.spacing9xl} width={'100%'}>
					<Grid size={colSize}>
						<Stack gap={Dimens.spacingXl}>
							<InputField
								label={t('webhook.labels.thumbprint', { ns: 'entities' })}
								readonly
								disabled
								copyable
								value={certificate?.thumbprint ?? ''}
								onChange={() => {}}
							/>
							<InputField
								label={t('webhook.labels.rotationDate', { ns: 'entities' })}
								readonly
								disabled
								value={certificate?.expiryDate.toString()}
								onChange={() => {}}
							/>
							{certificate && (
								<FileCard
									fileName={certificate.fileName}
									fileSize={certificate.readableFileSize}
									fileType='pem'
									label={t('webhook.labels.downloadCertificate', { ns: 'entities' })}
									onDownloadClicked={() => {
										if (certificate?.file) {
											triggerBlobDownload(certificate?.file, certificate.fileName);
										}
									}}
								/>
							)}
						</Stack>
					</Grid>
				</Grid>
			),
		},
		{
			key: 'step 2',
			content: (
				<Grid container columnSpacing={Dimens.spacing9xl} width={'100%'}>
					<Grid size={colSize}>
						<Stack gap={Dimens.spacingXl}>
							<Typography variant='label' color={palette.textPrimary.main}>
								{t('webhook.labels.identityProviderConfiguration', { ns: 'entities' })}
							</Typography>
							<InputField
								label={t('webhook.labels.clientId', { ns: 'entities' })}
								required
								hasError={hasSubmitted && clientId.length == 0}
								value={clientId}
								onChange={setClientId}
							/>
							<InputField
								label={t('webhook.labels.scope', { ns: 'entities' })}
								required
								value={scope}
								hasError={hasSubmitted && scope.length == 0}
								onChange={setScope}
							/>
							<InputField
								label={t('webhook.labels.tokenEndpoint', { ns: 'entities' })}
								required
								value={tokenEndpoint}
								hasError={hasSubmitted && tokenEndpoint.length == 0}
								prefix='https://'
								onChange={setTokenEndpoint}
							/>
						</Stack>
					</Grid>
					<Grid size={colSize}>
						<Stack gap={Dimens.spacingXl}>
							<Typography variant='label' color={palette.textPrimary.main}>
								{t('webhook.labels.webhookConfiguration', { ns: 'entities' })}
							</Typography>
							<InputField
								label={t('webhook.labels.webhookEndpoint', { ns: 'entities' })}
								value={webhookEndpoint}
								prefix='https://'
								required
								hasError={hasSubmitted && webhookEndpoint.length == 0}
								onChange={setWebhookEndpoint}
							/>
						</Stack>
					</Grid>
				</Grid>
			),
		},
	];

	const wasSuccess = updateError === undefined;
	const resultSubmitLabel = t(wasSuccess ? 'continue' : 'change', { ns: 'actions' });
	const resultIcon =
		wasSuccess ?
			<VectorImage name='icon/check' height='32' width='32' />
		:	<VectorImage name='icon/cross' height='32' width='32' />;
	const resultTitle = t(
		wasSuccess ? 'webhook.labels.successfullyIntegrated' : 'webhook.labels.integrationFailed',
		{ ns: 'entities' }
	);

	return (
		<>
			<Card>
				<Stepper steps={steps} currentStep={currentPage} />
				<Stack
					direction='row'
					gap={Dimens.spacingSm}
					alignItems={'center'}
					sx={{ mt: Dimens.spacing4xl, width: 'fit-content' }}
				>
					<Typography variant='labelLg' color={palette.textPrimary.main}>
						{steps[currentPage]}
					</Typography>
					{currentPage == 1 && (
						<Tooltip
							content={t('webhook.tooltips.configuration', { ns: 'entities' })}
							placement='top'
						>
							<VectorImage
								name='icon/info-circle'
								color={palette.borderPrimarySolid.main}
								height={16}
								width={16}
							/>
						</Tooltip>
					)}
				</Stack>
				<Pager
					sx={{ width: '100%', mt: Dimens.spacingXl }}
					currentPage={currentPage}
					onPageChanged={setCurrentPage}
					onFinish={createWebhook}
					pages={pages}
					isLoading={isUpdatingWebhook}
				/>
			</Card>

			<CustomDialog
				open={showWebhookResultDialog}
				title={resultTitle}
				icon={resultIcon}
				confirmButtonText={resultSubmitLabel}
				cancellable={false}
				variant={wasSuccess ? StatusVariant.Success : StatusVariant.Destructive}
				onConfirm={() => {
					if (!wasSuccess) setShowWebhookResultDialog(false);
					else navigate('/' + pagePathSegments.Webhooks);
				}}
				onClose={() => setShowWebhookResultDialog(false)}
				onDecline={() => setShowWebhookResultDialog(false)}
				sx={{ p: Dimens.spacingMd }}
			>
				{updateError?.errorCode === 'validationError' ?
					<NotificationCard
						title={updateError.getDescription()}
						statusVariant={StatusVariant.Destructive}
					/>
				:	<Stack gap={Dimens.spacing3xl}>
						{updateError?.errorCode === 'WHK_001' ?
							<NotificationCard
								title={t('webhook.labels.configurationCorrect', { ns: 'entities' })}
								caption={updateError?.detail}
								statusVariant={StatusVariant.Destructive}
							/>
						:	<NotificationCard
								title={t('webhook.labels.configurationCorrect', { ns: 'entities' })}
								statusVariant={StatusVariant.Success}
							/>
						}
						{updateError?.errorCode === 'WHK_002' ?
							<NotificationCard
								title={t('webhook.labels.omitAuthorization', { ns: 'entities' })}
								caption={updateError?.detail}
								statusVariant={StatusVariant.Destructive}
							/>
						:	<NotificationCard
								title={t('webhook.labels.omitAuthorization', { ns: 'entities' })}
								statusVariant={StatusVariant.Success}
							/>
						}
						{updateError?.errorCode === 'WHK_003' ?
							<NotificationCard
								title={t('webhook.labels.errorCouldNotBeProcessed', { ns: 'entities' })}
								caption={updateError?.detail}
								statusVariant={StatusVariant.Destructive}
							/>
						:	<NotificationCard
								title={t('webhook.labels.errorProcessed', { ns: 'entities' })}
								statusVariant={StatusVariant.Success}
							/>
						}
					</Stack>
				}
			</CustomDialog>
		</>
	);
};

export default CreateWebhookContainer;
